import { useEffect, useState } from 'react';
import IApplicationReview from 'interfaces/IApplicationReview';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_APPLICATION_REVIEW, UPDATE_APPLICATION_REVIEW } from 'graphql/member';
import { UpdateApplicationReviewCommandInput } from '../../../../graphql/__generated__/graphql';
import { OperationResult } from '../../../../interfaces/OperationResult';

const useApplicationReview = (reviewId: string) => {
    const client = useApolloClient();

    const [applicationReview, setApplicationReview] = useState<IApplicationReview>();
    const [updating, setUpdating] = useState(false);

    const { data, loading, error } = useQuery(GET_APPLICATION_REVIEW, {
        variables: { reviewId },
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (!loading && !error && data?.accreditationApplicationReview) {
            setApplicationReview((prevState) => {
                return {
                    ...prevState,
                    ...data.accreditationApplicationReview,
                };
            });
        }
    }, [data, loading, error]);

    const update = async (applicationReview: IApplicationReview): Promise<OperationResult> => {
        const review = {
            applicationReviewId: applicationReview.applicationReviewId,
            jsonData: applicationReview.jsonData,
            status: applicationReview.reviewStatus,
            reviewFormat: applicationReview.reviewFormat,
        } as UpdateApplicationReviewCommandInput;

        try {
            setUpdating(true);
            await client.mutate({
                mutation: UPDATE_APPLICATION_REVIEW,
                variables: { review },
            });

            setApplicationReview(applicationReview);
            return { success: true, message: 'Review updated successfully' };
        } catch (e) {
            console.error('Error updating review', e);
            return {
                success: false,
                message:
                    'An error occurred while updating the review. Please try again or refresh the page. Your review has been backed up.',
            };
        } finally {
            setUpdating(false);
        }
    };

    return {
        models: {
            applicationReview,
            loading,
            updating,
        },
        operations: {
            update,
        },
    };
};

export default useApplicationReview;
